import { type FormEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FounderBrandMark } from "../../components/founder/FounderBrandMark";
import { PasswordInputWithToggle } from "../../components/PasswordInputWithToggle";
import founderApi, { type ApiResponse } from "../../services/founderApi";
import { resolveAuthErrorMessage } from "../../services/publisherAuth";

type SetupResponse = {
  email: string;
};

export function FounderSetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState(
    token ? "" : "This password setup link is invalid or has expired.",
  );
  const [submitting, setSubmitting] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || !token) {
      return;
    }

    if (password.length < 16) {
      setFormError("Choose a password of at least 16 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setFormError("The two passwords do not match.");
      return;
    }

    setFormError("");
    setSubmitting(true);
    try {
      const response = await founderApi.post<ApiResponse<SetupResponse>>(
        "/auth/founder/password-setup",
        { token, newPassword: password },
      );
      setSavedEmail(response.data.data.email);
    } catch (error: unknown) {
      setFormError(resolveAuthErrorMessage(error, "login"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="oac-founder-login">
      <article aria-label="OpenAppCart Founder" className="oac-founder-login__card">
        <header className="oac-founder-login__header">
          <FounderBrandMark asLink={false} subIdentity="Founder" subIdentityInline />
        </header>

        {savedEmail ? (
          <div className="oac-founder-login__form">
            <p className="oac-founder-login__helper">
              Password saved for {savedEmail}. Sign in with this email and the password you just
              chose. Later password changes use My Account → Login &amp; security.
            </p>
            <button
              className="oac-founder-login__submit"
              type="button"
              onClick={() => navigate("/founder/log-in")}
            >
              Continue to sign in
            </button>
          </div>
        ) : (
          <form className="oac-founder-login__form" onSubmit={handleSubmit}>
            <p className="oac-founder-login__helper">
              Choose a private password (at least 16 characters). This link works once and expires
              in two hours.
            </p>
            <div className="oac-founder-login__field">
              <label htmlFor="founder-set-password">New password</label>
              <PasswordInputWithToggle
                autoComplete="new-password"
                id="founder-set-password"
                name="password"
                onChange={(event) => setPassword(event.target.value)}
                required
                value={password}
              />
            </div>
            <div className="oac-founder-login__field">
              <label htmlFor="founder-set-password-confirm">Confirm password</label>
              <PasswordInputWithToggle
                autoComplete="new-password"
                id="founder-set-password-confirm"
                name="confirmPassword"
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                value={confirmPassword}
              />
            </div>
            {formError ? (
              <p aria-live="polite" className="oac-founder-login__error">
                {formError}
              </p>
            ) : null}
            <button
              className="oac-founder-login__submit"
              disabled={submitting || !token}
              type="submit"
            >
              {submitting ? "Please wait..." : "Save password"}
            </button>
          </form>
        )}
      </article>
    </div>
  );
}
